import { useEffect, useState } from 'react';

import { faSquareList } from '@fortawesome/pro-regular-svg-icons';
import { createHeadlessEditor } from '@lexical/headless';

import { Permissions, TTemplatedResponse } from '@cloud-wave/neon-common-lib';

import { useConfigContext } from 'lib/core/config';
import { useAuthContext } from 'lib/core/context/AuthProvider';

import { usePermissionsContext } from 'lib/common/contexts/PermissionsContext';
import { useSoftphoneModalContext } from 'lib/common/contexts/SoftphoneModalContext';
import { useLayout } from 'lib/common/contexts/layout/LayoutContext';

import ClickableIcon from 'lib/common/components/ClickableIcon';

import requestErrorToast from 'lib/common/utils/toast/requestErrorToast';

import Popover from '../Popover';
import { Content } from './TemplatedResponseContent';
import { TemplatedResponseProps } from './TemplatedResponseTypes';
import { getTemplatedResponses } from './api/getTemplatedResponses';

import './styles/overrides.scss';

const ICON_SIZE = 17;
const DEFAULT_POPOVER_OFFSET = 10;

export default function TemplatedResponse({ type, queueIds = [], onClick, contentOnly }: TemplatedResponseProps) {
  const { config } = useConfigContext();
  const { fetch_ } = useAuthContext();
  const { isSoftphone } = useLayout();
  const { hasPermission } = usePermissionsContext();
  const [templates, setTemplates] = useState<TTemplatedResponse[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const {
    actions: { openModal, closeModal },
  } = useSoftphoneModalContext();

  // Getting text content from editor state needs a headless editor, but it throws an
  // error because it can't find any nodes because they don't exist because it's a headless
  //  editor. Hence, we're silencing those errors
  const editor = createHeadlessEditor({ onError: () => {} });

  const canUseTemplates = hasPermission({
    type: 'tenant',
    permission: Permissions.TEMPLATED_RESPONSE,
  });

  //Fetch on first render
  useEffect(() => {
    if (!canUseTemplates) {
      return;
    }
    (async () => {
      try {
        const templatesRes = await getTemplatedResponses({ config, type, queueIds, fetch_ });
        setTemplates(templatesRes);
        setLoading(false);
      } catch (e: any) {
        requestErrorToast({ errorReference: e.requestId });
      }
    })();
  }, []);

  if (!canUseTemplates) {
    return null;
  }

  const offsets = isSoftphone ? { offset: 0, offsetLeft: 0 } : { offset: 20, offsetLeft: 0 };

  const contentProps = {
    templates,
    type,
    editor,
    isSoftphone,
    onClick,
  };

  // this is used to when calling the component from the SmsBlock
  if (contentOnly) {
    return <Content {...contentProps} loading={loading} />;
  }

  if (isSoftphone) {
    return (
      <ClickableIcon
        icon={faSquareList}
        onClick={() => {
          openModal({
            content: (
              <Content {...contentProps} withHeader={false} loading={loading} closePopoverOrModal={closeModal} />
            ),
            title: 'Insert A Response',
          });
        }}
        size={ICON_SIZE}
        data-testid="templated-response-item"
        tooltip="Templates"
      />
    );
  }

  return (
    <Popover
      anchor={
        <ClickableIcon
          icon={faSquareList}
          onClick={() => {}}
          size={ICON_SIZE}
          data-testid="templated-response-item"
          tooltip="Templates"
        />
      }
      reverse
      transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      offset={-1 * (offsets.offset + DEFAULT_POPOVER_OFFSET)}
      offsetLeft={offsets.offsetLeft}
    >
      {({ onClose }) => <Content {...contentProps} loading={loading} closePopoverOrModal={onClose} />}
    </Popover>
  );
}
